import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import ReplyTemplateManageModal from '@features/ReplyTemplateManageModal';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import { MultiSelect } from '@components/MultiSelect';
import { Typography } from '@components/Typography';
import ComboLocationMultiselect, { LOCATION_TYPE } from '@features/ComboLocationMultiselect';
import { autoreplyRulesService } from '@services';
import { AccIntegrationName } from '@constants/integrations';
import { REVIEW_TYPE, AUTOREPLY_TYPE } from '@constants/autoreply-rules';
import { alertActions, autoreplyRulesActions, replyTemplatesActions } from '@actions';
import { useTranslation, useYupValidationResolver, useLocations } from '@hooks';
import { handleApiError } from '@helpers/api';
import { Modal, Content, RadioButtons } from './styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
    DELETE: 'DELETE',
    VIEW: 'VIEW',
};

const ratingOptions = [
    { value: 1, label: '1' },
    { value: 2, label: '2' },
    { value: 3, label: '3' },
    { value: 4, label: '4' },
    { value: 5, label: '5' },
];

const EditModal = ({ selectedRule, setSelectedRule, modal, setModal, brands, page, setPage, rowsPerPage }) => {
    const [replyTemplateModal, setReplyTemplateModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const replyTemplates = useSelector(state => state.replyTemplates.data);
    const account = useSelector(state => state.account?.account);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getLocationsOptions } = useLocations();

    const reviewTypesOptions = [
        { value: REVIEW_TYPE.WITHOUT_COMMENT, label: t('ReviewsAutoreplies.withoutComment') },
        { value: REVIEW_TYPE.WITH_COMMENT, label: t('ReviewsAutoreplies.withComment') },
    ];

    const AutoreplyRuleSchema = yup.object().shape(
        {
            name: yup.string().required('validation.required'),
            locations: yup.array().when(['locationType', 'brandAccess'], {
                is: (value, brandAccess) =>
                    value === LOCATION_TYPE.LOCATIONS || (value === LOCATION_TYPE.BRANDS && !brandAccess),
                then: schema => schema.min(1, 'validation.required'),
            }),
            brands: yup.array().when(['locationType', 'brandAccess'], {
                is: (value, brandAccess) => value === LOCATION_TYPE.BRANDS && brandAccess,
                then: schema => schema.min(1, 'validation.required'),
            }),
            ratings: yup.array().min(1, 'validation.required'),
            reviewTypes: yup.array().min(1, 'validation.required'),
            templates: yup.array().when('type', {
                is: value => value === AUTOREPLY_TYPE.TEMPLATES,
                then: schema => schema.min(1, 'validation.required'),
                otherwise: schema => schema.notRequired(),
            }),
            aiCustomInstructions: yup.string().when('aiCustomInstructions', {
                is: val => !val?.length,
                then: schema => schema.notRequired(),
                otherwise: schema => schema.max(500, 'validation.maxLength'),
            }),
            locationType: yup.string(),
        },
        [['aiCustomInstructions', 'aiCustomInstructions']],
    );

    const resolver = useYupValidationResolver(AutoreplyRuleSchema);

    const {
        getValues,
        setValue,
        control,
        handleSubmit,
        watch,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'onBlur',
        resolver,
        defaultValues: {
            name: '',
            locations: [],
            brands: [],
            ratings: [],
            reviewTypes: [],
            type: AUTOREPLY_TYPE.TEMPLATES,
            templates: [],
            applyToPastReviews: false,
            autoReplyEnabled: false,
            sendRecoveryForm: false,
            locationType: LOCATION_TYPE.BRANDS,
            aiCustomInstructions: '',
        },
    });

    const autoReplyType = watch('type');
    const selectedRatings = watch('ratings');
    const isFiveStarOnlyRule = selectedRatings?.length === 1 && selectedRatings[0] === 5;

    const locationsOptions = useMemo(() => {
        const filterCb = l => l?.profiles?.some(p => p.type === AccIntegrationName.Google);
        return getLocationsOptions(filterCb);
    }, []);

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));

    const templatesOptions = replyTemplates.map(template => ({ value: template.id, label: template.name }));

    useEffect(() => {
        const isNewAutoreplyRule = modal.type === FORM_TYPE.NEW;
        if (!isNewAutoreplyRule) {
            setValue('name', selectedRule?.name || '', { shouldValidate: true });
            setValue('ratings', selectedRule?.ratings || [1, 2, 3, 4, 5], {
                shouldValidate: true,
            });
            setValue(
                'reviewTypes',
                selectedRule?.reviewTypes || [REVIEW_TYPE.WITHOUT_COMMENT, REVIEW_TYPE.WITH_COMMENT],
                { shouldValidate: true },
            );
            setValue('type', selectedRule?.type || AUTOREPLY_TYPE.TEMPLATES, { shouldValidate: true });
            setValue('autoReplyEnabled', !!selectedRule?.autoReplyEnabled, { shouldValidate: true });
            setValue('sendRecoveryForm', !!selectedRule?.sendRecoveryForm, { shouldValidate: true });
            setValue('templates', selectedRule?.templates || [], {
                shouldValidate: true,
            });
            setValue('applyToPastReviews', !!selectedRule?.applyToPastReviews, { shouldValidate: true });
            setValue('aiCustomInstructions', selectedRule?.aiCustomInstructions || '', { shouldValidate: true });
        }
    }, [selectedRule, setValue, modal.type]);

    const refetchReplyTemplatesCallback = () => dispatch(replyTemplatesActions.getAll());

    const onClose = () => {
        setSelectedRule(null);
        setModal({ open: false, type: FORM_TYPE.NEW });
    };

    const refetchFirstPage = async () => {
        if (page === 0) {
            await dispatch(autoreplyRulesActions.get(page, rowsPerPage));
        } else {
            setPage(0); // INFO: Triggers refetch of templates
        }
    };

    const REFETCH_MAP = {
        [FORM_TYPE.EDIT]: async () => {
            await dispatch(autoreplyRulesActions.get(page, rowsPerPage));
        },
        [FORM_TYPE.NEW]: () => refetchFirstPage(),
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [FORM_TYPE.NEW]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const {
            name,
            locations,
            brands,
            ratings,
            reviewTypes,
            templates,
            applyToPastReviews,
            autoReplyEnabled,
            sendRecoveryForm,
            type,
            aiCustomInstructions,
        } = getValues();
        try {
            await autoreplyRulesService.addOrUpdateAutoreplyRules({
                name,
                locations: locations.map(l => l.id),
                brands,
                ratings,
                reviewTypes,
                type,
                ...(type === AUTOREPLY_TYPE.TEMPLATES && { templates, applyToPastReviews }),
                ...(type === AUTOREPLY_TYPE.AI && {
                    autoReplyEnabled,
                    aiCustomInstructions,
                    sendRecoveryForm: sendRecoveryForm && !isFiveStarOnlyRule,
                }),
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedRule.id }),
            });
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
            return;
        }

        dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
        REFETCH_MAP[modal.type]();
        onClose();
    };

    const isView = modal.type === FORM_TYPE.VIEW;

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={
                modal.type === FORM_TYPE.NEW
                    ? t('ReviewsAutoreplies.addRule')
                    : modal.type === FORM_TYPE.VIEW
                      ? t('ReviewsAutoreplies.viewRule')
                      : t('ReviewsAutoreplies.editRule')
            }
            onPrimaryAction={isView ? null : handleSubmit(onSubmit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty}
        >
            <Content>
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('ReviewsAutoreplies.ruleName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                            disabled={isView}
                            sx={{ gridArea: 'area1' }}
                        />
                    )}
                />
                <FormProvider {...{ control, setValue, watch }}>
                    <ComboLocationMultiselect
                        brandsOptions={brandsOptions}
                        locationsOptions={locationsOptions}
                        module="reviews"
                        disabled={isView}
                        data={selectedRule}
                        isView={isView}
                    />
                </FormProvider>
                <Controller
                    control={control}
                    name="ratings"
                    render={({ field, fieldState: { error }, ref }) => (
                        <MultiSelect
                            {...field}
                            ref={ref}
                            onChange={value => setValue('ratings', value, { shouldValidate: true, shouldDirty: true })}
                            options={ratingOptions}
                            label={t('common.rating')}
                            style={{ gridArea: 'area4' }}
                            fullWidth
                            countable
                            required
                            error={error}
                            disabled={isView}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="reviewTypes"
                    render={({ field, fieldState: { error }, ref }) => (
                        <MultiSelect
                            {...field}
                            ref={ref}
                            onChange={value =>
                                setValue('reviewTypes', value, { shouldValidate: true, shouldDirty: true })
                            }
                            options={reviewTypesOptions}
                            label={t('ReviewsAutoreplies.reviewType')}
                            style={{ gridArea: 'area5' }}
                            fullWidth
                            required
                            countable
                            error={error}
                            disabled={isView}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="type"
                    render={({ field }) => (
                        <RadioButtons
                            value={field.value}
                            onChange={value => setValue('type', value, { shouldDirty: true, shouldValidate: true })}
                            options={[
                                { value: AUTOREPLY_TYPE.TEMPLATES, label: t('common.templates') },
                                { value: AUTOREPLY_TYPE.AI, label: 'Pulse AI' },
                            ]}
                        />
                    )}
                />
                {autoReplyType === AUTOREPLY_TYPE.TEMPLATES ? (
                    <>
                        <Controller
                            control={control}
                            name="templates"
                            render={({ field, fieldState: { error }, ref }) => {
                                return (
                                    <MultiSelect
                                        {...field}
                                        ref={ref}
                                        onChange={value =>
                                            setValue('templates', value, { shouldValidate: true, shouldDirty: true })
                                        }
                                        options={templatesOptions}
                                        label={t('common.replyTemplates')}
                                        style={{ gridArea: 'area7' }}
                                        fullWidth
                                        countable
                                        required
                                        error={error}
                                        disabled={isView}
                                        creatable
                                        creatableText={t('ReviewsAutoreplies.createNewTemplate')}
                                        creatableClick={() =>
                                            setReplyTemplateModal({ open: true, type: FORM_TYPE.NEW })
                                        }
                                    />
                                );
                            }}
                        />
                        <Controller
                            control={control}
                            name="applyToPastReviews"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={getValues('applyToPastReviews')}
                                    handleChange={event =>
                                        setValue('applyToPastReviews', event.target.checked, { shouldDirty: true })
                                    }
                                    label={t('ReviewsAutoreplies.applyToPastReviews')}
                                    disabled={isView}
                                    style={{ gridArea: 'area8' }}
                                />
                            )}
                        />
                    </>
                ) : (
                    <div style={{ gridArea: 'area7' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'block',
                                paddingBottom: '16px',
                            }}
                        >
                            {t('ReviewsAutoreplies.pulseAiDescription')}
                        </Typography>
                        <Controller
                            control={control}
                            name="aiCustomInstructions"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{
                                        ...field,
                                        onChange: e => field.onChange(e.target.value),
                                    }}
                                    label={t('common.customInstructionsOpt')}
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxLength={500}
                                    error={!!error}
                                    helperText={error ? t(error.message, { count: 500 }) : null}
                                    disabled={isView}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="autoReplyEnabled"
                            render={({ field, ref }) => (
                                <Switch
                                    {...field}
                                    ref={ref}
                                    checked={field.value}
                                    handleChange={event =>
                                        setValue('autoReplyEnabled', event.target.checked, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        })
                                    }
                                    label={t('ReviewsAutoreplies.enableAI')}
                                    disabled={isView}
                                    style={{ marginTop: '16px' }}
                                />
                            )}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'var(--text-secondary)',
                                display: 'block',
                                paddingTop: '8px',
                            }}
                        >
                            {t('ReviewsAutoreplies.enableAIDescription')}
                        </Typography>
                        {account?.surveysEnabled && (
                            <>
                                <Controller
                                    control={control}
                                    name="sendRecoveryForm"
                                    render={({ field, ref }) => (
                                        <Switch
                                            {...field}
                                            ref={ref}
                                            checked={field.value && !isFiveStarOnlyRule}
                                            handleChange={event =>
                                                setValue('sendRecoveryForm', event.target.checked, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                })
                                            }
                                            label={t('ReviewsAutoreplies.sendRecoveryForm')}
                                            disabled={isView || isFiveStarOnlyRule}
                                            style={{ marginTop: '16px' }}
                                        />
                                    )}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'var(--text-secondary)',
                                        display: 'block',
                                        paddingTop: '8px',
                                    }}
                                >
                                    {t('ReviewsAutoreplies.sendRecoveryFormDescription')}
                                </Typography>
                            </>
                        )}
                    </div>
                )}
            </Content>
            {replyTemplateModal.open ? (
                <ReplyTemplateManageModal
                    modal={replyTemplateModal}
                    onCloseModal={() => setReplyTemplateModal({ open: false, type: FORM_TYPE.NEW })}
                    refetchCallback={refetchReplyTemplatesCallback}
                />
            ) : null}
        </Modal>
    );
};

export default EditModal;
